export class EventsOverview extends HTMLElement {
    private currentItems: any[] = []
    private pastItems: any[] = []
    private pastHasMore = false
    private pastOffset = 0
    private isLoadingCurrent = false
    private isLoadingPast = false

    private readonly PAGE_SIZE = 9
    private readonly TODAY_ATTR = 'data-today'
    private readonly NO_RESULTS_TEXT_ATTR = 'data-no-results-text'
    private readonly LOAD_MORE_TEXT_ATTR = 'data-load-more-text'
    private readonly ARIA_LABEL_TEMPLATE_ATTR = 'data-more-link-aria-label-template'

    connectedCallback() {
        this.fetchAll()
    }

    fetchPast(today: string, append: boolean) {
        if (this.isLoadingPast) return
        this.isLoadingPast = true

        const pastList = this.querySelector('.past-list')
        if (pastList) pastList.setAttribute('aria-busy', 'true')

        if (!append) {
            this.pastOffset = 0
            this.pastItems = []
        }

        const yesterday = this.getYesterday(today)

        fetch('/api/entries', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
                this.buildPayload({
                    to: yesterday,
                    limit: this.PAGE_SIZE,
                    offset: this.pastOffset,
                    showPast: true,
                }),
            ),
        })
            .then((r) => r.json())
            .then((result: any) => {
                this.isLoadingPast = false
                const newItems = (result?.items ?? []).map(this.mapEntry)
                this.pastItems = append ? [...this.pastItems, ...newItems] : newItems
                this.pastOffset = this.pastItems.length
                this.pastHasMore = result?.hasMore === true
                this.renderPast(newItems, append)
            })
            .catch(() => {
                this.isLoadingPast = false
                this.pastHasMore = false
                this.renderPast([], append)
            })
    }

    private formatDateForDisplay(dateStr: string): string {
        if (!dateStr) return ''
        const parts = dateStr.split('-')
        if (parts.length !== 3) return dateStr
        const [y, m, d] = parts.map(Number)
        return `${d}.${m}.${y}`
    }

    private getYesterday(today: string): string {
        const d = new Date(today + 'T00:00:00')
        d.setDate(d.getDate() - 1)
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
    }

    private fetchAll() {
        const today = this.getAttribute(this.TODAY_ATTR) ?? this.getTodayString()
        this.fetchCurrent(today)
        this.fetchPast(today, false)
    }

    private getTodayString(): string {
        const d = new Date()
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
    }

    private buildPayload(extra: Record<string, any>): any {
        return { section: 'events', ...extra }
    }

    private fetchCurrent(today: string) {
        if (this.isLoadingCurrent) return
        this.isLoadingCurrent = true

        const currentList = this.querySelector('.current-list')
        if (currentList) currentList.setAttribute('aria-busy', 'true')

        fetch('/api/entries', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(this.buildPayload({ from: today, limit: 0 })),
        })
            .then((r) => r.json())
            .then((result: any) => {
                this.isLoadingCurrent = false
                this.currentItems = (result?.items ?? []).map(this.mapEntry)
                this.renderCurrent()
            })
            .catch(() => {
                this.isLoadingCurrent = false
                this.currentItems = []
                this.renderCurrent()
            })
    }

    private mapEntry = (entry: any) => ({
        title: entry.title || '',
        date: entry.date || '',
        tags: Array.isArray(entry.tags) ? entry.tags : [],
        text: entry.teaserText || entry.mehrzeiligerText || '',
        url: entry.url || '',
    })

    private renderCard(item: any, ariaLabelTemplate: string): string {
        const ariaLabel = ariaLabelTemplate.replace('{title}', item.title)
        const tagsHtml = item.tags.length > 0 ? item.tags.map((tag: any) => `<span class="oi-tag hug-arrow__label">${tag.title}</span>`).join('') : ''

        return `
            <a
                href="${item.url}"
                class="teaser group col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3"
                aria-label="${ariaLabel}"
            >
                <div class="flex justify-end items-start">
                    <span class="button shrink-0 self-start" aria-hidden="true">
                        <i class="internal-icon"></i>
                    </span>
                </div>
                <div class="grow flex flex-col gap-y-teaserGap">
                    <div class="flex flex-col gap-y-eventY">
                        <div class="text-date">${this.formatDateForDisplay(item.date)}</div>
                        <h3 class="title-h3 !text-eventtitle hyphens-auto w-full !mb-0 leading-[1.05em]">${item.title}</h3>
                        ${item.text ? `<p class="text-teasertext w-full !m-0">${item.text}</p>` : ''}
                    </div>
                    <div class="oi-tags" aria-hidden="true">
                        ${tagsHtml}
                    </div>
                </div>
            </a>
        `
    }

    private renderCurrent() {
        const section = this.querySelector('.current-events-section')
        const list = this.querySelector('.current-list')
        if (!section || !list) return

        list.setAttribute('aria-busy', 'false')

        const ariaLabelTemplate = this.getAttribute(this.ARIA_LABEL_TEMPLATE_ATTR) ?? 'Event aufrufen: {title}'

        if (this.currentItems.length > 0) {
            section.classList.remove('hidden')
            list.innerHTML = this.currentItems.map((item) => this.renderCard(item, ariaLabelTemplate)).join('')
        } else {
            section.classList.add('hidden')
            list.innerHTML = ''
        }
    }

    private renderPast(newItems: any[], append: boolean) {
        const list = this.querySelector('.past-list')
        const loadMoreContainer = this.querySelector('.load-more-container')
        if (!list) return

        list.setAttribute('aria-busy', 'false')

        const noResultsText = this.getAttribute(this.NO_RESULTS_TEXT_ATTR) ?? 'Keine Events gefunden.'
        const loadMoreText = this.getAttribute(this.LOAD_MORE_TEXT_ATTR) ?? 'Mehr anzeigen'
        const ariaLabelTemplate = this.getAttribute(this.ARIA_LABEL_TEMPLATE_ATTR) ?? 'Event aufrufen: {title}'

        if (!append) {
            if (this.pastItems.length > 0) {
                list.innerHTML = this.pastItems.map((item) => this.renderCard(item, ariaLabelTemplate)).join('')
            } else {
                list.innerHTML = `<li class="col-span-full py-8 text-base">${noResultsText}</li>`
            }
        } else if (newItems.length > 0) {
            list.insertAdjacentHTML('beforeend', newItems.map((item) => this.renderCard(item, ariaLabelTemplate)).join(''))
        }

        if (loadMoreContainer) {
            if (this.pastHasMore) {
                const today = this.getAttribute(this.TODAY_ATTR) ?? this.getTodayString()
                loadMoreContainer.innerHTML = `
                    <button type="button" class="load-more-btn group mx-auto flex flex-col items-center cursor-pointer text-bodysmall text-darkgrey">
                        <span class="text-loadmore">${loadMoreText}</span>
                        <span class="flex items-center mt-listMt justify-center rounded-full border border-darkgrey py-[var(--sp-loadmore-py)] px-[var(--sp-loadmore-px)] transition-colors duration-200 group-hover:bg-green group-hover:border-green">
                            <svg width="24" height="24" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg" class="rotate-90 transition-transform duration-500 group-hover:translate-y-1" aria-hidden="true">
                                <path d="M15.0252 23.6878L26.6924 12.0205L15.0252 0.353243M26.6924 12.0205L0 12.0205" stroke="currentColor"/>
                            </svg>
                        </span>
                    </button>
                `
                loadMoreContainer.querySelector('button')?.addEventListener('click', () => {
                    this.fetchPast(today, true)
                })
            } else {
                loadMoreContainer.innerHTML = ''
            }
        }
    }
}
